import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { cargarOficiales, yo, type Yo } from '@/api';
import {
  getConfig,
  isUnlocked,
  loadConfig,
  loadFreeExplains,
  restore,
  unlockWith,
  type Config,
} from '@/premium';

interface AppContextValue {
  config: Config;
  me: Yo | null;
  loading: boolean;
  premium: boolean;
  online: boolean;
  theme: 'light' | 'dark';
  refreshMe: () => Promise<Yo>;
  activateLicense: (key?: string) => Promise<number>;
  toggleTheme: () => void;
}

const AppContext = createContext<AppContextValue | null>(null);

function preferredTheme(): 'light' | 'dark' {
  const saved = localStorage.getItem('permiso-vtc.theme');
  if (saved === 'light' || saved === 'dark') return saved;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function AppProvider({ children }: PropsWithChildren) {
  const [config, setConfig] = useState<Config>(getConfig());
  const [me, setMe] = useState<Yo | null>(null);
  const [loading, setLoading] = useState(true);
  const [premium, setPremium] = useState(isUnlocked());
  const [online, setOnline] = useState(navigator.onLine);
  const [theme, setTheme] = useState<'light' | 'dark'>(preferredTheme);

  const refreshMe = useCallback(async () => {
    const next = await yo();
    setMe(next);
    if (next.usuario?.verificado) await cargarOficiales();
    return next;
  }, []);

  const activateLicense = useCallback(async (key = '') => {
    const added = await unlockWith(key);
    setPremium(true);
    await refreshMe().catch(() => null);
    return added;
  }, [refreshMe]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem('permiso-vtc.theme', theme);
  }, [theme]);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      const [nextConfig, nextMe] = await Promise.all([
        loadConfig(),
        yo().catch(() => null),
        loadFreeExplains(),
      ]);
      if (!active) return;
      setConfig(nextConfig);
      setMe(nextMe);

      if (nextMe?.usuario?.verificado) await cargarOficiales();
      if (nextMe?.licencia) {
        await unlockWith('').catch(() => 0);
      } else {
        await restore();
      }
      if (!active) return;
      setPremium(isUnlocked());
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo<AppContextValue>(
    () => ({
      config,
      me,
      loading,
      premium,
      online,
      theme,
      refreshMe,
      activateLicense,
      toggleTheme: () => setTheme((current) => (current === 'dark' ? 'light' : 'dark')),
    }),
    [activateLicense, config, loading, me, online, premium, refreshMe, theme],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error('useApp debe usarse dentro de AppProvider');
  return value;
}
